import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Download } from "lucide-react";
import AdminLayout from "@/admin/AdminLayout";
import { adminApi } from "@/admin/AdminAuth";
import { toast } from "sonner";

export default function AdminSubscribers() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/subscribers?per_page=200");
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (s) => {
    const next = s.status === "active" ? "unsubscribed" : "active";
    await adminApi.patch(`/admin/subscribers/${s.id}`, { status: next });
    setData((prev) => ({
      ...prev,
      items: prev.items.map((x) => (x.id === s.id ? { ...x, status: next } : x)),
    }));
  };

  const exportCsv = async () => {
    try {
      const res = await adminApi.get("/admin/subscribers/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `peptichill_subscribers_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch {
      toast.error("Export failed.");
    }
  };

  return (
    <AdminLayout title="Newsletter subscribers">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted">
          <strong className="text-navy" data-testid="admin-subscribers-count">{data.total}</strong> subscribers
        </p>
        <button
          type="button"
          onClick={exportCsv}
          data-testid="admin-subscribers-export"
          className="inline-flex items-center gap-2 rounded-full bg-navy text-white px-5 h-10 text-sm font-semibold hover:brightness-110 transition shadow-card"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="rounded-2xl bg-white border border-silver/40 shadow-card overflow-hidden">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-muted text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading…
          </div>
        ) : data.items.length === 0 ? (
          <div className="p-12 text-center text-muted text-sm">No subscribers yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-subscribers-table">
            <thead className="bg-ice-light/60 text-muted text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Subscribed</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((s) => (
                <tr key={s.id} className="border-t border-silver/30 hover:bg-ice-light/40">
                  <td className="px-4 py-3 text-navy font-medium">{s.email}</td>
                  <td className="px-4 py-3 text-muted">{(s.subscribed_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-muted">{s.source}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize ${
                      s.status === "active" ? "bg-cyan-soft text-navy" : "bg-silver/40 text-ink/70"
                    }`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => toggleStatus(s)}
                      className="text-xs text-navy hover:text-cyan font-semibold"
                      data-testid={`admin-subscriber-toggle-${s.id}`}
                    >
                      {s.status === "active" ? "Unsubscribe" : "Reactivate"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}
